import type { ComponentType } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowRight,
  BookOpenCheck,
  Boxes,
  DatabaseZap,
  FileSearch,
  GitBranch,
  Network,
  UploadCloud,
} from 'lucide-react'
import type { Document, ReadinessSummary } from '@agentwiki/shared'
import type { ChipTone } from '../hybrid'
import { DocumentResultTerms } from '../shared/document-result-metadata'
import { buildDocumentResultPresentation } from '../../lib/document-result-presentation'
import { formatSourcePath } from '../../lib/document-kind'

export interface WorkspaceKnowledgeMetric {
  label: string
  value: number
  detail: string
  href?: string
  tone: ChipTone
  icon: ComponentType<{ className?: string }>
}

export interface WorkspaceKnowledgeStage {
  label: string
  metric: string
  detail: string
  icon: ComponentType<{ className?: string }>
}

export function formatNumber(value: number | undefined) {
  return new Intl.NumberFormat('en-US').format(value ?? 0)
}

export function verifiedCount(summary: ReadinessSummary | undefined, fallback: number) {
  if (!summary) return fallback
  return Math.max(
    0,
    summary.sources.documentsWithSource -
      summary.sources.staleCount -
      summary.sources.conflictCount,
  )
}

export function compileModeLabel(summary?: ReadinessSummary) {
  const modes = [
    summary?.sources.preserveCount
      ? `${formatNumber(summary.sources.preserveCount)} preserve`
      : null,
    summary?.sources.normalizeCount
      ? `${formatNumber(summary.sources.normalizeCount)} normalize`
      : null,
    summary?.sources.synthesizeCount
      ? `${formatNumber(summary.sources.synthesizeCount)} synthesize`
      : null,
  ].filter(Boolean)
  return modes.length > 0 ? modes.join(' · ') : 'No compiled sources yet'
}

export function buildWorkspaceKnowledgeMetrics({
  verified,
  needsReview,
  uploadStatusUnavailable,
  sourceBacked,
  sourceCount,
  memoryItems,
  edgeCount,
}: {
  verified: number
  needsReview: number
  uploadStatusUnavailable: boolean
  sourceBacked: number
  sourceCount?: number
  memoryItems: number
  edgeCount?: number
}): WorkspaceKnowledgeMetric[] {
  return [
    {
      label: 'Synced source docs',
      value: verified,
      detail: 'Ready to read',
      href: '/doc?ownership=source-backed',
      tone: verified > 0 ? 'ok' : 'mut',
      icon: BookOpenCheck,
    },
    {
      label: 'Operational warnings',
      value: needsReview,
      detail: uploadStatusUnavailable ? 'Upload status unknown' : 'Sync or extraction warnings',
      tone: needsReview > 0 || uploadStatusUnavailable ? 'warn' : 'ok',
      icon: AlertTriangle,
    },
    {
      label: 'Source-backed',
      value: sourceBacked,
      detail: `${formatNumber(sourceCount)} synced sources`,
      href: '/doc?ownership=source-backed',
      tone: sourceBacked > 0 ? 'proc' : 'mut',
      icon: UploadCloud,
    },
    {
      label: 'Knowledge items',
      value: memoryItems,
      detail: `${formatNumber(edgeCount)} graph edges`,
      href: '/search',
      tone: memoryItems > 0 ? 'agent' : 'mut',
      icon: Boxes,
    },
  ]
}

export function buildWorkspaceKnowledgeStages(
  summary?: ReadinessSummary,
): WorkspaceKnowledgeStage[] {
  return [
    {
      label: 'Sources',
      metric: `${formatNumber(summary?.sources.sourceCount)} synced`,
      detail: `${formatNumber(summary?.files.total)} raw files`,
      icon: UploadCloud,
    },
    {
      label: 'Compile',
      metric: `${formatNumber(summary?.workspace.documentCount)} docs`,
      detail: compileModeLabel(summary),
      icon: FileSearch,
    },
    {
      label: 'Extract',
      metric: `${formatNumber(summary?.memory.total)} memory`,
      detail: `${formatNumber(summary?.files.extracted)} extracted files`,
      icon: DatabaseZap,
    },
    {
      label: 'Serve',
      metric: `${formatNumber(summary?.agents.activeApiKeys)} keys`,
      detail: 'Docs · Search · Graph · MCP',
      icon: Network,
    },
  ]
}

function docPath(doc: Document) {
  return `/doc/${doc.slug || doc.id}`
}

function docExcerpt(doc: Document) {
  return doc.summary || doc.description || formatSourcePath(doc.sourceProvenance?.sourcePath, 96)
}

export function WorkspaceKnowledgeDocRow({ doc }: { doc: Document }) {
  const presentation = buildDocumentResultPresentation(doc)

  return (
    <Link to={docPath(doc)} className="aw-workspace-doc-row">
      <span className="aw-workspace-doc-icon" aria-hidden>
        <BookOpenCheck className="h-4 w-4" />
      </span>
      <span className="aw-workspace-doc-main">
        <span className="aw-workspace-doc-title">
          <strong>{doc.title || 'Untitled'}</strong>
          <DocumentResultTerms presentation={presentation} />
        </span>
        <span className="aw-workspace-doc-excerpt">{docExcerpt(doc)}</span>
        <span className="aw-workspace-doc-meta">
          <span>
            <GitBranch className="h-3.5 w-3.5" />
            {presentation.source.label}
          </span>
          <span>{presentation.memoryClassLabel ?? 'No memory class'}</span>
        </span>
      </span>
      <ArrowRight className="h-4 w-4" aria-hidden />
    </Link>
  )
}
